import React, { useState, useEffect } from 'react';
import '../../styles/AdminPages.css';
import './GestionFilieres.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import filiereService from '../../services/filiereService';

function GestionFilieres() {
  const [filieres, setFilieres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    icon: '',
    duree: '',
    taux_reussite: '',
    debouches: [''],
    competences: [''],
    images: [],
    active: true
  });

  useEffect(() => {
    loadFilieres();
  }, []);

  const loadFilieres = async () => {
    try {
      setLoading(true);
      const response = await filiereService.admin.getAll();
      setFilieres(response.data);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement des filières');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs sauf les arrays et images
      Object.keys(formData).forEach(key => {
        if (key === 'images') {
          // Ajouter chaque image séparément
          formData.images.forEach((image, index) => {
            if (image) {
              formDataToSend.append(`images[${index}]`, image);
            }
          });
        } else if (key === 'debouches') {
          const debouches = formData.debouches.filter(d => d.trim() !== '');
          formDataToSend.append(key, JSON.stringify(debouches));
        } else if (key === 'competences') {
          const competences = formData.competences.filter(c => c.trim() !== '');
          formDataToSend.append(key, JSON.stringify(competences));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (editingId) {
        await filiereService.admin.update(editingId, formDataToSend);
        alert('Filière mise à jour avec succès');
      } else {
        await filiereService.admin.create(formDataToSend);
        alert('Filière créée avec succès');
      }
      
      setShowModal(false);
      resetForm();
      loadFilieres();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await filiereService.admin.getById(id);
      const filiere = response.data;
      setFormData({
        titre: filiere.titre,
        description: filiere.description,
        icon: filiere.icon || '',
        duree: filiere.duree,
        taux_reussite: filiere.taux_reussite || '',
        debouches: filiere.debouches || [''],
        competences: filiere.competences || [''],
        images: [],
        active: filiere.active
      });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement de la filière');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette filière ?')) {
      return;
    }
    try {
      await filiereService.admin.delete(id);
      alert('Filière supprimée avec succès');
      loadFilieres();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      description: '',
      icon: '',
      duree: '',
      taux_reussite: '',
      debouches: [''],
      competences: [''],
      images: [],
      active: true
    });
    setEditingId(null);
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    if (field === 'images') {
      setFormData({ ...formData, [field]: [...formData[field], null] });
    } else {
      setFormData({ ...formData, [field]: [...formData[field], ''] });
    }
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Filières" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="page-header">
            <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              + Nouvelle Filière
            </button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="filieres-grid">
              {filieres.map((filiere) => (
                <div key={filiere.id} className="filiere-card">
                  <div className="filiere-header">
                    <h3>{filiere.titre}</h3>
                    <span className={`badge ${filiere.active ? 'badge-success' : 'badge-draft'}`}>
                      {filiere.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="filiere-description">{filiere.description}</p>
                  <div className="filiere-meta">
                    <span>Durée: {filiere.duree}</span>
                    {filiere.taux_reussite && (
                      <span>Taux de réussite: {filiere.taux_reussite}%</span>
                    )}
                  </div>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(filiere.id)}>
                      Modifier
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(filiere.id)}>
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingId ? 'Modifier la filière' : 'Nouvelle filière'}</h2>
                  <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                </div>
                <form onSubmit={handleSubmit} className="filiere-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Titre *</label>
                      <input
                        type="text"
                        value={formData.titre}
                        onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Durée *</label>
                      <input
                        type="text"
                        value={formData.duree}
                        onChange={(e) => setFormData({ ...formData, duree: e.target.value })}
                        placeholder="Ex: 3 ans"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="4"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Icône (emoji ou URL)</label>
                      <input
                        type="text"
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        placeholder="Ex: 🎨 ou https://..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Taux de réussite (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.taux_reussite}
                        onChange={(e) => setFormData({ ...formData, taux_reussite: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Débouchés</label>
                    {formData.debouches.map((debouche, index) => (
                      <div key={index} className="array-field">
                        <input
                          type="text"
                          value={debouche}
                          onChange={(e) => handleArrayFieldChange('debouches', index, e.target.value)}
                          placeholder="Débouché..."
                        />
                        {formData.debouches.length > 1 && (
                          <button type="button" onClick={() => removeArrayField('debouches', index)}>×</button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('debouches')}>
                      + Ajouter un débouché
                    </button>
                  </div>

                  <div className="form-group">
                    <label>Compétences</label>
                    {formData.competences.map((competence, index) => (
                      <div key={index} className="array-field">
                        <input
                          type="text"
                          value={competence}
                          onChange={(e) => handleArrayFieldChange('competences', index, e.target.value)}
                          placeholder="Compétence..."
                        />
                        {formData.competences.length > 1 && (
                          <button type="button" onClick={() => removeArrayField('competences', index)}>×</button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('competences')}>
                      + Ajouter une compétence
                    </button>
                  </div>

                  <div className="form-group">
                    <label>Images</label>
                    {formData.images.map((image, index) => (
                      <div key={index} className="array-field">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleArrayFieldChange('images', index, e.target.files[0])}
                        />
                        {formData.images.length > 1 && (
                          <button type="button" onClick={() => removeArrayField('images', index)}>×</button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('images')}>
                      + Ajouter une image
                    </button>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      />
                      Filière active
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                      Annuler
                    </button>
                    <button type="submit" className="btn-submit">
                      {editingId ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default GestionFilieres;